import {
  collection, doc, addDoc, updateDoc, deleteDoc, getDocs,
  onSnapshot, query, orderBy, writeBatch, where
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, functions, storage } from './firebaseService';
import { compressImages } from '../utils/imageCompression';
import { Vehicle, VehicleEquipment, VehicleCategory, TowVehicleInfo, TerrainPreference, AppUser } from '../types';

const vehiclesCollection = (uid: string) =>
  collection(db, 'users', uid, 'vehicles');

// ── Subscribe ────────────────────────────────────────────────────────

export function subscribeToVehicles(
  uid: string,
  callback: (vehicles: Vehicle[]) => void
): () => void {
  const q = query(vehiclesCollection(uid), orderBy('created_at', 'asc'));
  return onSnapshot(q, (snap) => {
    const vehicles = snap.docs.map(d => ({ id: d.id, ...d.data() } as Vehicle));
    callback(vehicles);
  }, () => callback([]));
}

// ── Vehicle Snapshot ─────────────────────────────────────────────────

/** Build a compact vehicle snapshot from the user's active vehicle denorm fields.
 *  Returns null if the user has no active vehicle. */
export function buildVehicleSnapshot(user: AppUser) {
  if (!user.activeVehicleCategory) return null;
  return {
    category: user.activeVehicleCategory,
    length: user.activeVehicleLength || undefined,
    is4wd: user.activeVehicleEquipment?.four_wheel_drive || undefined,
  };
}

// ── Helpers ──────────────────────────────────────────────────────────

/** Categories that can have a tow vehicle */
const TOWABLE_CATEGORIES: VehicleCategory[] = ['caravan', 'camper_trailer'];

/** Sync denormalized active-vehicle fields to the user doc.
 *  For trailers with a linked tow vehicle, calculates combined dimensions. */
async function syncActiveVehicleToUserDoc(
  batch: ReturnType<typeof writeBatch>,
  uid: string,
  vehicle: Pick<Vehicle, 'id' | 'category' | 'length_cm' | 'width_cm' | 'weight_kg' | 'equipment' | 'tow_vehicle' | 'terrain_preferences' | 'road_filters' | 'photo_url' | 'ai_avatar_svg' | 'marker_color'> | null
) {
  const userRef = doc(db, 'users', uid);
  if (vehicle) {
    const isTowable = TOWABLE_CATEGORIES.includes(vehicle.category);
    const tow = isTowable ? vehicle.tow_vehicle : undefined;

    let length = vehicle.length_cm || null;
    let width = vehicle.width_cm || null;
    let weight = vehicle.weight_kg || null;
    const equipment = { ...vehicle.equipment };

    if (tow) {
      // Combined length = tow + trailer
      if (tow.length_cm && vehicle.length_cm) {
        length = tow.length_cm + vehicle.length_cm;
      } else if (tow.length_cm) {
        length = tow.length_cm;
      }
      // Combined width = max of both
      if (tow.width_cm || vehicle.width_cm) {
        width = Math.max(tow.width_cm || 0, vehicle.width_cm || 0);
      }
      // Combined weight = sum
      if (tow.weight_kg || vehicle.weight_kg) {
        weight = (tow.weight_kg || 0) + (vehicle.weight_kg || 0);
      }
      // Merge equipment (OR logic)
      if (tow.is_ev) equipment.is_ev = true;
      if (tow.four_wheel_drive) equipment.four_wheel_drive = true;
    }

    batch.update(userRef, {
      activeVehicleId: vehicle.id,
      activeVehicleCategory: vehicle.category,
      activeVehicleLength: length,
      activeVehicleWidth: width,
      activeVehicleWeight: weight,
      activeVehicleEquipment: equipment,
      activeVehicleTerrainPrefs: vehicle.terrain_preferences || null,
      activeVehicleRoadFilters: vehicle.road_filters || null,
      activeVehiclePhotoUrl: vehicle.photo_url || null,
      activeVehicleAiAvatarSvg: vehicle.ai_avatar_svg || null,
      activeVehicleHasTow: isTowable && !!tow?.make_model,
      vehicleMarkerColor: vehicle.marker_color || null,
    });
  } else {
    batch.update(userRef, {
      activeVehicleId: null,
      activeVehicleCategory: null,
      activeVehicleLength: null,
      activeVehicleWidth: null,
      activeVehicleWeight: null,
      activeVehicleEquipment: null,
      activeVehicleTerrainPrefs: null,
      activeVehicleRoadFilters: null,
      activeVehiclePhotoUrl: null,
      activeVehicleAiAvatarSvg: null,
      activeVehicleHasTow: null,
      vehicleMarkerColor: null,
    });
  }
}

/** Clear is_default on all vehicles that currently have it */
async function clearAllDefaults(
  batch: ReturnType<typeof writeBatch>,
  uid: string
) {
  const q = query(vehiclesCollection(uid), where('is_default', '==', true));
  const snap = await getDocs(q);
  snap.docs.forEach(d => {
    batch.update(d.ref, { is_default: false });
  });
}

// ── Add ──────────────────────────────────────────────────────────────

export async function addVehicle(
  uid: string,
  data: Omit<Vehicle, 'id'>
): Promise<string> {
  const batch = writeBatch(db);

  if (data.is_default) {
    await clearAllDefaults(batch, uid);
  }

  // addDoc doesn't work with batches, so we create a ref manually
  const newRef = doc(vehiclesCollection(uid));
  batch.set(newRef, {
    nickname: data.nickname,
    make_model: data.make_model,
    registration_number: data.registration_number || null,
    category: data.category,
    length_cm: data.length_cm || null,
    width_cm: data.width_cm || null,
    weight_kg: data.weight_kg || null,
    equipment: data.equipment,
    is_default: data.is_default,
    created_at: data.created_at,
    tow_vehicle: data.tow_vehicle || null,
    terrain_preferences: data.terrain_preferences || null,
    marker_color: data.marker_color || null,
  });

  if (data.is_default) {
    await syncActiveVehicleToUserDoc(batch, uid, {
      id: newRef.id,
      category: data.category,
      length_cm: data.length_cm,
      width_cm: data.width_cm,
      weight_kg: data.weight_kg,
      equipment: data.equipment,
      tow_vehicle: data.tow_vehicle,
      terrain_preferences: data.terrain_preferences,
      photo_url: data.photo_url,
      ai_avatar_svg: data.ai_avatar_svg,
      marker_color: data.marker_color,
      road_filters: data.road_filters,
    });
  }

  await batch.commit();
  return newRef.id;
}

// ── Update ───────────────────────────────────────────────────────────

export async function updateVehicle(
  uid: string,
  vehicleId: string,
  updates: Partial<Omit<Vehicle, 'id' | 'created_at'>>
): Promise<void> {
  const batch = writeBatch(db);
  const vRef = doc(db, 'users', uid, 'vehicles', vehicleId);

  // If setting this one as default, clear others
  if (updates.is_default === true) {
    await clearAllDefaults(batch, uid);
  }

  batch.update(vRef, updates as Record<string, any>);

  // If this vehicle is/becomes default, sync to user doc
  if (updates.is_default === true) {
    // We need the full vehicle data to sync — read current + merge updates
    const snap = await getDocs(query(vehiclesCollection(uid)));
    const current = snap.docs.find(d => d.id === vehicleId);
    const merged = { ...current?.data(), ...updates } as Vehicle;
    await syncActiveVehicleToUserDoc(batch, uid, {
      id: vehicleId,
      category: merged.category,
      length_cm: merged.length_cm,
      width_cm: merged.width_cm,
      weight_kg: merged.weight_kg,
      equipment: merged.equipment,
      tow_vehicle: merged.tow_vehicle,
      terrain_preferences: merged.terrain_preferences,
      photo_url: merged.photo_url,
      ai_avatar_svg: merged.ai_avatar_svg,
      marker_color: merged.marker_color,
      road_filters: merged.road_filters,
    });
  }

  await batch.commit();
}

// ── Set Default ──────────────────────────────────────────────────────

export async function setDefaultVehicle(
  uid: string,
  vehicleId: string
): Promise<void> {
  const batch = writeBatch(db);

  // Read all vehicles first to check retired status
  const allSnap = await getDocs(query(vehiclesCollection(uid)));
  const target = allSnap.docs.find(d => d.id === vehicleId);
  if (target?.data()?.is_retired) {
    throw new Error('Cannot set a retired vehicle as default');
  }

  await clearAllDefaults(batch, uid);

  const vRef = doc(db, 'users', uid, 'vehicles', vehicleId);
  batch.update(vRef, { is_default: true });
  if (target) {
    const data = target.data() as Vehicle;
    await syncActiveVehicleToUserDoc(batch, uid, {
      id: vehicleId,
      category: data.category,
      length_cm: data.length_cm,
      width_cm: data.width_cm,
      weight_kg: data.weight_kg,
      equipment: data.equipment,
      tow_vehicle: data.tow_vehicle,
      terrain_preferences: data.terrain_preferences,
      photo_url: data.photo_url,
      ai_avatar_svg: data.ai_avatar_svg,
      marker_color: data.marker_color,
      road_filters: data.road_filters,
    });
  }

  await batch.commit();
}

// ── Delete ───────────────────────────────────────────────────────────

export async function deleteVehicle(
  uid: string,
  vehicleId: string
): Promise<void> {
  const batch = writeBatch(db);
  const vRef = doc(db, 'users', uid, 'vehicles', vehicleId);

  // Check if this was the default
  const allSnap = await getDocs(query(vehiclesCollection(uid)));
  const targetDoc = allSnap.docs.find(d => d.id === vehicleId);
  const wasDefault = targetDoc?.data()?.is_default === true;

  batch.delete(vRef);

  if (wasDefault) {
    // Promote the oldest remaining vehicle
    const remaining = allSnap.docs
      .filter(d => d.id !== vehicleId)
      .sort((a, b) => (a.data().created_at || 0) - (b.data().created_at || 0));

    if (remaining.length > 0) {
      const next = remaining[0];
      batch.update(next.ref, { is_default: true });
      const nextData = next.data() as Vehicle;
      await syncActiveVehicleToUserDoc(batch, uid, {
        id: next.id,
        category: nextData.category,
        length_cm: nextData.length_cm,
        width_cm: nextData.width_cm,
        weight_kg: nextData.weight_kg,
        equipment: nextData.equipment,
        tow_vehicle: nextData.tow_vehicle,
        terrain_preferences: nextData.terrain_preferences,
        photo_url: nextData.photo_url,
        ai_avatar_svg: nextData.ai_avatar_svg,
        marker_color: nextData.marker_color,
        road_filters: nextData.road_filters,
      });
    } else {
      // No vehicles left — clear user doc
      await syncActiveVehicleToUserDoc(batch, uid, null);
    }
  }

  await batch.commit();
}

// ── Tow Vehicle (Vogntog) ────────────────────────────────────────────

/** Update or set tow vehicle info on a trailer */
export async function updateTowVehicle(
  uid: string,
  vehicleId: string,
  towData: TowVehicleInfo
): Promise<void> {
  const batch = writeBatch(db);
  const vRef = doc(db, 'users', uid, 'vehicles', vehicleId);
  batch.update(vRef, { tow_vehicle: towData });

  // If this vehicle is the default, re-sync to user doc with combined dimensions
  const allSnap = await getDocs(query(vehiclesCollection(uid)));
  const target = allSnap.docs.find(d => d.id === vehicleId);
  if (target?.data()?.is_default) {
    const data = target.data() as Vehicle;
    await syncActiveVehicleToUserDoc(batch, uid, {
      id: vehicleId,
      category: data.category,
      length_cm: data.length_cm,
      width_cm: data.width_cm,
      weight_kg: data.weight_kg,
      equipment: data.equipment,
      tow_vehicle: towData,
      terrain_preferences: data.terrain_preferences,
      photo_url: data.photo_url,
      ai_avatar_svg: data.ai_avatar_svg,
      marker_color: data.marker_color,
      road_filters: data.road_filters,
    });
  }

  await batch.commit();
}

/** Remove tow vehicle from a trailer */
export async function removeTowVehicle(
  uid: string,
  vehicleId: string
): Promise<void> {
  const batch = writeBatch(db);
  const vRef = doc(db, 'users', uid, 'vehicles', vehicleId);
  batch.update(vRef, { tow_vehicle: null });

  // If this vehicle is the default, re-sync without tow data
  const allSnap = await getDocs(query(vehiclesCollection(uid)));
  const target = allSnap.docs.find(d => d.id === vehicleId);
  if (target?.data()?.is_default) {
    const data = target.data() as Vehicle;
    await syncActiveVehicleToUserDoc(batch, uid, {
      id: vehicleId,
      category: data.category,
      length_cm: data.length_cm,
      width_cm: data.width_cm,
      weight_kg: data.weight_kg,
      equipment: data.equipment,
      tow_vehicle: undefined,
      terrain_preferences: data.terrain_preferences,
      photo_url: data.photo_url,
      ai_avatar_svg: data.ai_avatar_svg,
      marker_color: data.marker_color,
      road_filters: data.road_filters,
    });
  }

  await batch.commit();
}

// ── SVV API Lookup ───────────────────────────────────────────────────

export interface SVVResult {
  ok: boolean;
  make: string | null;
  model: string | null;
  make_model: string | null;
  length_cm: number | null;
  width_cm: number | null;
  weight_kg: number | null;
  is_ev: boolean;
  is_4wd: boolean;
  vehicle_class: string | null;
  suggested_category: VehicleCategory | null;
  color: string | null;
  color_name: string | null;
}

export async function fetchVehicleDataFromSVV(
  registrationNumber: string
): Promise<SVVResult> {
  const callable = httpsCallable(functions, 'fetchVehicleData');
  const result = await callable({ registrationNumber });
  return result.data as SVVResult;
}

// ── Vehicle Photos ──────────────────────────────────────────────────

export async function uploadVehiclePhotos(
  uid: string,
  vehicleId: string,
  files: File[]
): Promise<string[]> {
  if (!uid || !vehicleId || files.length === 0) return [];
  const compressed = await compressImages(files);
  const urls: string[] = [];
  for (const file of compressed) {
    const safeName = file.name.replace(/\s+/g, '_');
    const fileRef = ref(storage, `vehicles/${uid}/${vehicleId}_${Date.now()}_${safeName}`);
    const snap = await uploadBytes(fileRef, file);
    urls.push(await getDownloadURL(snap.ref));
  }
  return urls;
}

export async function deleteVehiclePhoto(photoUrl: string): Promise<void> {
  try {
    const fileRef = ref(storage, photoUrl);
    await deleteObject(fileRef);
  } catch (err) {
    console.warn('Could not delete vehicle photo from storage:', err);
  }
}

// ── Retire / Un-retire ──────────────────────────────────────────────

export async function retireVehicle(
  uid: string,
  vehicleId: string
): Promise<void> {
  const batch = writeBatch(db);
  const vRef = doc(db, 'users', uid, 'vehicles', vehicleId);

  const allSnap = await getDocs(query(vehiclesCollection(uid)));
  const targetDoc = allSnap.docs.find(d => d.id === vehicleId);
  const wasDefault = targetDoc?.data()?.is_default === true;

  batch.update(vRef, {
    is_retired: true,
    retired_at: Date.now(),
    is_default: false,
  });

  if (wasDefault) {
    const remaining = allSnap.docs
      .filter(d => d.id !== vehicleId && !d.data().is_retired)
      .sort((a, b) => (a.data().created_at || 0) - (b.data().created_at || 0));

    if (remaining.length > 0) {
      const next = remaining[0];
      batch.update(next.ref, { is_default: true });
      const nextData = next.data() as Vehicle;
      await syncActiveVehicleToUserDoc(batch, uid, {
        id: next.id,
        category: nextData.category,
        length_cm: nextData.length_cm,
        width_cm: nextData.width_cm,
        weight_kg: nextData.weight_kg,
        equipment: nextData.equipment,
        tow_vehicle: nextData.tow_vehicle,
        terrain_preferences: nextData.terrain_preferences,
        photo_url: nextData.photo_url,
        ai_avatar_svg: nextData.ai_avatar_svg,
        marker_color: nextData.marker_color,
        road_filters: nextData.road_filters,
      });
    } else {
      await syncActiveVehicleToUserDoc(batch, uid, null);
    }
  }

  await batch.commit();
}

export async function unretireVehicle(
  uid: string,
  vehicleId: string
): Promise<void> {
  const vRef = doc(db, 'users', uid, 'vehicles', vehicleId);
  await updateDoc(vRef, {
    is_retired: false,
    retired_at: null,
  });
}

// ── AI Avatar Generation ────────────────────────────────────────────

export interface AiAvatarResult {
  ok: boolean;
  svg: string;
}

/** Generate an AI avatar for a vehicle based on its make/model */
export async function generateVehicleAvatarAI(
  vehicleId: string
): Promise<AiAvatarResult> {
  const callable = httpsCallable(functions, 'generateVehicleAvatar', { timeout: 180000 });
  const result = await callable({ vehicleId });
  return result.data as AiAvatarResult;
}

/** Admin: Backfill vehicle colors from SVV for all registered vehicles */
export async function backfillVehicleColors(): Promise<any> {
  const callable = httpsCallable(functions, 'backfillVehicleColors', { timeout: 300000 });
  const result = await callable({});
  return result.data;
}

/** Re-sync the default vehicle fields to the user doc.
 *  Useful when new denormalized fields are added and need to be populated. */
export async function resyncDefaultVehicle(uid: string): Promise<void> {
  const q = query(vehiclesCollection(uid), where('is_default', '==', true));
  const snap = await getDocs(q);
  if (snap.empty) {
    console.log('No default vehicle found');
    return;
  }
  const vDoc = snap.docs[0];
  const data = vDoc.data() as Vehicle;
  console.log('Re-syncing default vehicle:', vDoc.id, data.nickname, {
    marker_color: data.marker_color,
    tow_vehicle: data.tow_vehicle?.make_model,
    category: data.category,
  });
  const batch = writeBatch(db);
  await syncActiveVehicleToUserDoc(batch, uid, {
    id: vDoc.id,
    category: data.category,
    length_cm: data.length_cm,
    width_cm: data.width_cm,
    weight_kg: data.weight_kg,
    equipment: data.equipment,
    tow_vehicle: data.tow_vehicle,
    terrain_preferences: data.terrain_preferences,
    photo_url: data.photo_url,
    ai_avatar_svg: data.ai_avatar_svg,
    marker_color: data.marker_color,
    road_filters: data.road_filters,
  });
  await batch.commit();
  console.log('Re-sync complete! vehicleMarkerColor:', data.marker_color, 'activeVehicleHasTow:', TOWABLE_CATEGORIES.includes(data.category) && !!data.tow_vehicle?.make_model);
}

// Expose for admin use from browser console
if (typeof window !== 'undefined') {
  (window as any).backfillVehicleColors = backfillVehicleColors;
  (window as any).resyncDefaultVehicle = resyncDefaultVehicle;
}
